import { Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";
import { T } from "react-translator-component";
import {
  Box, ClickAwayListener, Grow, MenuItem, MenuList,
  Paper, Popper, Tab, Tabs, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import SortIcon from '@mui/icons-material/Sort';
import { useAppDispatch } from "../../redux/store";
import { setErrorObj } from "../redux/errorReducer";
import LoadingModal from "../../components/LoadingModal";
import AdminPageFrame from "../components/AdminPageFrame";
import DropDown from "../components/DropDown";
import ReadMore from "../components/ReadMore";
import Separator from "../../components/Separator";
import Term, { TermRef } from "../components/Term";
import PaymentDetailModal from "../components/payments/PaymentDetailModal";
import { colors } from "../../common/colors";
import FetchButton from "../components/FetchButton";
import apiServices from "../services/apiServices";
import dayjs from "../../util/dayjs";
import { dispPaymentType, dispPayUserType } from "../util/payments";
import { payeeTypes, payerTypes, paymentTypes } from "../constants/payments";

type Form = {
  startDate: Dayjs,
  endDate: Dayjs,
  paymentType: string,
  payerType: string,
  payeeType: string,
}

type TabOption = {
  value: number,
  label: string,
}

export default function PaymentList() {
  const today = dayjs()
  const initForm: Form = {
    startDate: today,
    endDate: today,
    paymentType: "",
    payerType: "",
    payeeType: "",
  }

  const SEARCH_TAB_OPTIONS: TabOption[] = [
    {value: 1, label: T("mng_filtered_payments")},
    {value: 2, label: T("mng_payments_search_transaction_id")},
  ]

  const sortMenuOptions = [
    {value: "desc", name: T("mng_payments_sort_desc")},
    {value: "asc", name: T("mng_payments_sort_asc")},
  ]

  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectTab, setSelectTab] = useState<number>(1)
  const [showPaymentDetailModal, setShowPaymentDetailModal] = useState<boolean>(false);
  const [modalPayment, setModalPayment] = useState<any>();
  const [openDateSortOption, setOpenDateSortOption] = useState<boolean>(false);
  const [sortOption, setSortOption] = useState<"asc" | "desc">("desc");
  const dateAnchorRef = useRef<any>(null)

  const [searchTransactionId, setSearchTransactionId] = useState<string>("");
  const [form, setForm] = useState<Form>(initForm);
  const termRef = useRef<TermRef>(null)

  /* API関連 */
  const [paymentList, setPaymentList] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>({
    start_date: today.format("YYYY-MM-DD"),
    end_date: today.format("YYYY-MM-DD"),
    payment_type: "",
    payer_type: "",
    payee_type: "",
    sort_direction: sortOption,
    page: 1
  });
  const [pagination, setPagination] = useState<any>({});

  const createOptionList = (types: string[], prefix: string) => {
    const typeOptions = types.map((type: string) => {
      return {
        value: type,
        name: T(`${prefix}_${type.toLocaleLowerCase()}`),
      }
    })
    return [
      {value: "", name: T("mng_payments_filtered_reset")},
      ...typeOptions
    ]
  }

  const paymentTypeOptionList = createOptionList(paymentTypes, "mng_payment_type")
  const payerTypeOptionList = createOptionList(payerTypes, "mng_payment_person_type")
  const payeeTypeOptionList = createOptionList(payeeTypes, "mng_payment_person_type")

  const handleSelectSort = async (value: "asc" | "desc") => {
    setSortOption(value)
    setOpenDateSortOption(false)
  }

  useEffect(() => {
    if (!lastParams) return
    const params = {...lastParams, sort_direction: sortOption, page: 1}
    if (params) getPayments(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOption]);

  const createParams = (page?: number) => {
    if (selectTab === 1) {
      if (!termRef?.current?.validate()) return;
      const term = termRef?.current?.getTerm();

      setForm({
        ...form,
        startDate: term?.startDate,
        endDate: term?.endDate
      })

      const params = {
        start_date: term?.startDate?.format("YYYY-MM-DD"),
        end_date: term?.endDate?.format("YYYY-MM-DD"),
        payment_type: form.paymentType,
        payer_type: form.payerType,
        payee_type: form.payeeType,
        sort_direction: sortOption,
        page: page ?? 1
      }

      return params
    } else {
      const params = { ...lastParams, page: page ?? 1}
      return params
    }
  }

  const getPayments = async (params: any) => {
    try {
      setIsLoading(true);
      const res = await apiServices.getPayments(params);
      if (!params?.page || params.page === 1) {
        setPaymentList(res.payments);
      } else {
        setPaymentList([...paymentList, ...res.payments]);
      }
      setLastParams(params)
      setPagination(res.pagination);
    } catch (error: any) {
      dispatch(setErrorObj(error))
    } finally {
      setIsLoading(false);
    }
  };

  const openPaymentDetailModal = (payment: any) => {
    setModalPayment(payment)
    setShowPaymentDetailModal(true)
  }

  const closePaymentDetailModal = () => {
    setModalPayment(undefined)
    setShowPaymentDetailModal(false)
  }

  const handleMore = () => {
    if (!pagination?.next_page) return

    const params = {...lastParams, page: pagination.next_page}
    if (params) getPayments(params)
  };

  const handleSelectType =(type: "paymentType" | "payerType" | "payeeType", e: any) => {
    const value = e.target.value
    const newForm: Form = Object.assign({}, JSON.parse(JSON.stringify(form)));
    newForm[type] = value

    setForm(newForm)
  }

  const handleSearchPaymentList = async () => {
    const params = createParams()
    if (params) await getPayments(params)
    return false
  }

  const handleSearchTransactionId = async () => {
    if (!searchTransactionId) return false

    const params = {
      transaction_id: searchTransactionId,
    }
    await getPayments(params)
    return false
  }

  const handleSelectTab = (value: number) => {
    // 決済番号検索タブ押下時に日付入力内容を保持する
    if (value !== 1) {
      const term = termRef?.current?.getTerm();
      if (term?.startDate && term?.endDate) {
        setForm({
          ...form,
          startDate: term.startDate,
          endDate: term.endDate
        })
      }
    }

    setSelectTab(value)
  }

  return (
    <AdminPageFrame>
      <LoadingModal visible={isLoading}/>

      <TableContainer component={Paper} sx={styles.searchContainer}>
        <div style={styles.searchTitle}>{T("mng_search_area_title")}</div>
        <Tabs
          value={selectTab}
          onChange={(e, newValue) => handleSelectTab(newValue)}
          aria-label="wrapped label tabs example"
        >
          {SEARCH_TAB_OPTIONS.map((option: TabOption) => (
            <Tab value={option.value} label={option.label} key={option.label}/>
          ))}
        </Tabs>

        {selectTab === 1 ? (
          <div style={styles.m10}>
            <div style={styles.termBox}>
              <Typography style={styles.searchSectionName}>
                {T("mng_payment_date_term")}
              </Typography>

              <Term
                baseDate={today}
                initStartDate={form.startDate}
                initEndDate={form.endDate}
                vertical={false}
                ref={termRef}
              />
            </div>

            <div style={styles.dropDownsBox}>
              <div style={styles.selectPaymentType}>
                <DropDown
                  title={T("mng_payment_type")}
                  value={form.paymentType}
                  options={paymentTypeOptionList}
                  sx={styles.w100}
                  onChange={(e) => handleSelectType("paymentType", e)}
                />
              </div>

              <div style={styles.selectPaymentParty}>
                <DropDown
                  title={T("mng_payment_payer_info")}
                  value={form.payerType}
                  options={payerTypeOptionList}
                  sx={styles.w100}
                  onChange={(e) => handleSelectType("payerType", e)}
                />
              </div>

              <div style={styles.selectPaymentParty}>
                <DropDown
                  title={T("mng_payment_payee_info")}
                  value={form.payeeType}
                  options={payeeTypeOptionList}
                  sx={styles.w100}
                  onChange={(e) => handleSelectType("payeeType", e)}
                />
              </div>
            </div>

            <Box sx={styles.btncontainer}>
              <FetchButton
                title={T("mng_search")}
                onSubmit={async () => handleSearchPaymentList()}
              />
            </Box>
          </div>
        ) : (
          <>
            <div style={styles.m10}>
              <Typography style={styles.searchSectionName}>
                {T("mng_payment_transaction_id")}
              </Typography>

              <input
                type="text"
                placeholder={T("mng_payment_input_form_placeholder")}
                value={searchTransactionId}
                onChange={(e) => setSearchTransactionId(e.target.value)}
                style={styles.searchInputForm}
              />
            </div>

            <Box sx={styles.btncontainer}>
              <FetchButton
                title={T("mng_search")}
                onSubmit={async () => handleSearchTransactionId()}
              />
            </Box>
          </>
        )}
      </TableContainer>

      <TableContainer component={Paper} sx={styles.tableContainer}>
        <div aria-label="caption table">
          <div style={styles.tableTitle}>{T("mng_money_payment")}</div>
          <Separator color={colors.farm_gray100} />
        </div>

        <Table aria-label="caption table">
          <TableHead>
            <TableRow>
              <TableCell sx={{width: "13%"}}>{T("mng_payment_type")}</TableCell>
              <TableCell sx={{width: "18%"}}>{T("mng_payment_payer_info")}</TableCell>
              <TableCell sx={{width: "18%"}}>{T("mng_payment_payee_info")}</TableCell>
              <TableCell sx={{width: "8%"}}>{T("mng_payment_amount")}</TableCell>
              <TableCell>{T("mng_payment_transaction_id")}</TableCell>
              <TableCell
                ref={dateAnchorRef}
                onClick={() => setOpenDateSortOption(true)}
                sx={{width: "13%"}}
              >
                {T("mng_payment_date")}({T(`mng_payments_sort_${sortOption}`)})
                <SortIcon sx={{color: colors.farm_blue200}}/>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody sx={styles.p10}>
            {paymentList.map((payment: any, index: number) => (
              <TableRow key={index} onClick={() => openPaymentDetailModal(payment)}>
                <TableCell scope="row">
                  {dispPaymentType(payment.payment_type)}
                </TableCell>
                <TableCell sx={{overflowWrap: "anywhere"}}>
                  {dispPayUserType(payment.payer.type)}
                  <br />
                  {payment?.payer?.nickname ?? payment?.payer?.name}
                </TableCell>
                <TableCell sx={{overflowWrap: "anywhere"}}>
                  {dispPayUserType(payment.payee.type)}
                  <br />
                  {payment?.payee?.nickname ?? payment?.payee?.name}
                </TableCell>
                <TableCell scope="row">
                  {payment.formatted_amount.toLocaleString()}
                </TableCell>
                <TableCell scope="row">
                  {payment.transaction_id}
                </TableCell>
                <TableCell scope="row">
                  {dayjs(payment.created_at).format("YYYY/MM/DD HH:mm")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <ReadMore open={pagination.next_page > 0} sx={styles.paginate} onClick={() => handleMore()} />
      </TableContainer>

      <PaymentDetailModal
        open={showPaymentDetailModal}
        payment={modalPayment}
        onClose={closePaymentDetailModal}
      />

      <Popper
        open={openDateSortOption}
        anchorEl={dateAnchorRef.current}
        transition
      >
        {({ TransitionProps}) => (
          <Grow {...TransitionProps} style={{transformOrigin:'top'}}>
            <Paper>
              <ClickAwayListener onClickAway={() => setOpenDateSortOption(false)}>
                <MenuList>
                  {sortMenuOptions.map((option: any) => (
                    <MenuItem
                      key={option.value}
                      onClick={() => handleSelectSort(option.value)}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </AdminPageFrame>
  );
}

const styles = {
  searchContainer: {
    marginTop: 3,
    marginBottom: "10px",
    padding: "20px",
  },

  tableContainer: {
    marginBottom: "20px",
  },

  searchTitle: {
    padding: "10px 0",
  },

  searchSectionName: {
    color: "rgba(0, 0, 0, .6)",
    paddingBottom: 5
  },

  termBox: {
    margin: "0 10px",
  },

  dropDownsBox: {
    display: "flex",
    margin: "10px 0 0 10px"
  },

  selectPaymentType: {
    width: "20%",
    paddingTop: "10px",
  },

  selectPaymentParty: {
    width: "20%",
    marginLeft: "20px",
    paddingTop: "10px",
  },

  searchInputForm: {
    width: "100%",
    padding: "10px",
    marginBottom: "20px",
    border: "1px solid #ddd",
    borderRadius: "4px",
  },

  m10: {
    margin: "10px"
  },

  p10: {
    padding: "10px"
  },

  w100: {
    width: "100%"
  },

  tableTitle: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: "120px",
    paddingLeft: 10,
  },

  paginate: {
    width: "100%"
  },

  errorMessage: {
    margin: 0,
    fontFamily: "mplus-1c-regular"
  },

  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: 1
  },

  btncontainer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: 0
  },
}
